/**
 * BattleCode V2 — Visibility System
 *
 * Phase 2: 从 engine-scheduler.ts applyScanAction (L908-975) +
 *           applyVisibilityByDistance (L985-994) 抽出。
 * 视野计算逻辑不变，改为接收 BattleState + ctx 操作。
 *
 * FR-022 v1.0.3/v1.0.4 修订:
 *   - is_visible 改为每 tick 派生量（dist ≤ vision_range → true）
 *   - scan 语义 = 主动记录 enemy 最后已知位置 (last_known_position)
 *   - 超距 scan 写 spawn_point 兜底
 *   - P0 修复(2026-07-09): 玩家/AI scan 写入不同字段（lkp vs ai_known_player_position）
 */

import type { BattleState, BotAction } from "../types";
import { chebyshevDistance, TANK_STATS } from "../tank-stats";
import type { TickContext } from "../modes/game-mode.interface";

export class VisibilitySystem {
  /**
   * FR-018 ADR-018.6 / FR-022 v1.0.3 修订:处理 scan action
   * 迁移自 engine-scheduler.ts applyScanAction (L908-975)，行为完全一致。
   */
  static applyScan(
    state: BattleState,
    playerAction: BotAction,
    aiAction: BotAction,
    ctx: TickContext,
  ): void {
    if (playerAction.type !== "scan" && aiAction.type !== "scan") return;

    const dist = chebyshevDistance(state.self.position, state.enemy.position);
    const wasVisible = state.enemy.is_visible;
    const wasPlayerScanning = playerAction.type === "scan" && state.self.is_alive;
    const wasAIScanning = aiAction.type === "scan" && state.enemy.is_alive;

    // P0-#3:死人眼不存在
    if (!wasPlayerScanning && !wasAIScanning) return;

    if (dist > TANK_STATS.vision_range) {
      // P1-#4:超距 scan 失败
      // FR-022 v1.0.4:超距 scan 写 spawn_point 兜底
      // P0 修复:按扫描方写入不同字段
      if (wasPlayerScanning && state.enemy.last_known_position === null) {
        state.enemy.last_known_position = { ...state.enemy.spawn };
      } else if (wasAIScanning && (state.ai_known_player_position ?? null) === null) {
        state.ai_known_player_position = { ...state.self.spawn };
      }
      if (wasPlayerScanning) {
        ctx.emit({
          type: "key_event",
          eventType: "SCAN_FAILED",
          tickNum: ctx.tickNum,
          actor: "team_a",
          description: `玩家scan超距(dist=${dist},vision=${TANK_STATS.vision_range})`,
        });
      } else {
        ctx.emit({
          type: "key_event",
          eventType: "SCAN_FAILED",
          tickNum: ctx.tickNum,
          actor: "team_b",
          description: `AI scan超距(dist=${dist},vision=${TANK_STATS.vision_range})`,
        });
      }
      return;
    }

    // FR-022 v1.0.3 修订:scan 语义 = 主动记录 enemy 最后已知位置
    // P0-#1 修复:持续更新 last_known_position
    state.enemy.last_known_position = { ...state.enemy.position };

    // SCAN_DISCOVERY:任一方 scan + 之前不可见 → 触发
    if (!wasVisible) {
      const actor: "team_a" | "team_b" = wasPlayerScanning ? "team_a" : "team_b";
      const who = wasPlayerScanning ? "玩家" : "AI";
      ctx.emit({
        type: "key_event",
        eventType: "SCAN_DISCOVERY",
        tickNum: ctx.tickNum,
        actor,
        description: `${who}首次发现对方`,
      });
    }
  }

  /**
   * FR-022 v1.0.3 修订:is_visible 改为每 tick 派生量
   * 迁移自 engine-scheduler.ts applyVisibilityByDistance (L985-994)，行为完全一致。
   */
  static applyByDistance(state: BattleState): void {
    if (!state.self.is_alive || !state.enemy.is_alive) {
      state.enemy.is_visible = false;
      return;
    }
    const dist = chebyshevDistance(state.self.position, state.enemy.position);
    state.enemy.is_visible = dist <= TANK_STATS.vision_range;
  }
}
